import React, { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { BrandLogo } from "../../components/BrandLogo";
import { BuildInfoLabel } from "../../components/BuildInfoLabel";
import { useLoginViewModel } from "./LoginViewModel";
import { shouldShowOfflineLoginBanner } from "./loginUiGates";
import { Loader2, User, Lock, Eye, EyeOff, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useOfflinePos } from "../../../offline/OfflinePosContext";
import { supportsOfflinePos } from "../../../runtime/frontofficeRuntime";

const LoginScreen: React.FC = () => {
  const viewModel = useLoginViewModel();
  const { state, intents } = viewModel;
  const { username, password, branchId, rememberMe, isLoading, isLoadingBranches, branches, branchesAvailable, loginEnabled, errors, showBranchField, terminalSetup } = state;
  const {
    onUsernameChanged,
    onPasswordChanged,
    onBranchIdChanged,
    onRememberMeChanged,
    onFormSubmitted,
    onForgotPasswordClicked,
    isFormValid,
  } = intents;

  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const offlinePos = useOfflinePos();
  const showOfflineBanner = shouldShowOfflineLoginBanner({
    isOnline: offlinePos.isOnline,
  });
  const offlineCapable = supportsOfflinePos();

  // Wait for connectivity probe before choosing form vs first-launch message (no blink).
  if (!offlinePos.connectivityReady) {
    return (
      <div
        className="h-screen w-screen overflow-hidden flex items-center justify-center bg-brand-dark p-4"
        style={{
          backgroundImage:
            "radial-gradient(circle at 30% 55%, rgba(13,115,119,0.14) 0%, transparent 60%)",
        }}
      >
        <BuildInfoLabel tone="onDark" />
        <div className="text-center">
          <BrandLogo size="lg" inverted showTagline />
          <div className="mt-8 inline-block w-10 h-10 border-2 border-brand border-t-transparent rounded-full animate-spin" />
          <p className="mt-4 text-sm text-gray-400">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen w-screen overflow-y-auto flex items-start sm:items-center justify-center bg-brand-dark p-4 sm:p-8"
      style={{
        backgroundImage:
          "radial-gradient(circle at 30% 55%, rgba(13,115,119,0.14) 0%, transparent 60%)",
      }}
    >
      <BuildInfoLabel tone="onDark" />
      <div className="w-full max-w-md py-4">
        <div className="text-center mb-4 sm:mb-8">
          <BrandLogo size="lg" inverted showTagline />
        </div>

        <div className="bg-gray-900 rounded-2xl p-5 sm:p-8 border border-gray-800 shadow-2xl space-y-5">
          {offlineCapable && offlinePos.firstLaunchBlocked ? (
            <div
              className="rounded-xl border border-amber-700/50 bg-amber-950/40 px-3 py-3 text-sm text-amber-200"
              role="alert"
            >
              {offlinePos.firstLaunchMessage}
            </div>
          ) : (
            <>
          {showOfflineBanner && (
            <div className="rounded-xl border border-red-800/40 bg-red-950/30 px-3 py-2 text-xs text-red-200">
              {loginEnabled
                ? "Hors ligne — connexion disponible pour les comptes mémorisés (Se souvenir de moi)."
                : offlinePos.offlineLoginUnavailableMessage}
            </div>
          )}
          <div>
            <h2 className="text-lg font-semibold text-white">{t("login.title")}</h2>
            {terminalSetup && (
              <p className="text-sm text-gray-300 mt-1">
                {t("login.saved_terminal", {
                  branch: terminalSetup.branchName,
                  store: terminalSetup.storeName,
                })}
              </p>
            )}
            <p className="text-sm text-gray-500 mt-1">{t("login.subtitle")}</p>
          </div>

          <form onSubmit={onFormSubmitted} className="space-y-4">
            {showBranchField && (
            <div className="space-y-1.5">
              <Label htmlFor="branch" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {t("login.branch_label") || "Branch"} <span className="text-red-400">*</span>
              </Label>
              <div className="relative">
                <select
                  id="branch"
                  title="Select branch location"
                  value={branchId}
                  onChange={(e) => onBranchIdChanged(e.target.value)}
                  disabled={isLoading || isLoadingBranches || (!offlinePos.isOnline && !branchesAvailable)}
                  className={`w-full px-3 py-3 text-sm bg-gray-800 border border-gray-700 rounded-xl text-white appearance-none focus:ring-2 focus:ring-brand/40 focus:border-brand outline-none disabled:opacity-50 ${errors.branchId ? "border-red-500" : ""}`}
                >
                  {isLoadingBranches ? (
                    <option value="">{t('login.loading_branches')}</option>
                  ) : branches.length === 0 ? (
                    <option value="">No branches available</option>
                  ) : (
                    <>
                      <option value="">{t('login.select_branch')}</option>
                      {branches.map((branch) => (
                        <option key={branch.visibleId} value={branch.visibleId}>
                          {branch.name} ({branch.code})
                        </option>
                      ))}
                    </>
                  )}
                </select>
                <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
              </div>
              {errors.branchId && <p className="text-xs text-red-400">{errors.branchId}</p>}
            </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="username" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {t("login.username_label")} <span className="text-red-400">*</span>
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  id="username"
                  type="text"
                  placeholder={t("login.username_placeholder")}
                  value={username}
                  onChange={(e) => onUsernameChanged(e.target.value)}
                  disabled={isLoading || !loginEnabled}
                  className={`pl-10 h-12 bg-gray-800 border-gray-700 text-white placeholder:text-gray-600 rounded-xl focus-visible:ring-brand/40 ${errors.username ? "border-red-500" : ""}`}
                  autoComplete="username"
                />
              </div>
              {errors.username && <p className="text-xs text-red-400">{errors.username}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {t("login.password_label")} <span className="text-red-400">*</span>
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("login.password_placeholder")}
                  value={password}
                  onChange={(e) => onPasswordChanged(e.target.value)}
                  disabled={isLoading || !loginEnabled}
                  className={`pl-10 pr-10 h-12 bg-gray-800 border-gray-700 text-white placeholder:text-gray-600 rounded-xl focus-visible:ring-brand/40 ${errors.password ? "border-red-500" : ""}`}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-400">{errors.password}</p>}
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={(checked) => onRememberMeChanged(checked as boolean)}
                disabled={isLoading || (offlineCapable && !offlinePos.isOnline)}
                className="border-gray-600 data-[state=checked]:bg-brand data-[state=checked]:border-brand"
              />
              <Label htmlFor="remember" className="text-sm text-gray-400 font-normal">
                {t("login.remember_me")}
              </Label>
            </div>

            {errors.response && (
              <div
                className={`p-3 rounded-lg text-sm ${errors.response.includes("successful") ? "bg-emerald-950 text-emerald-400 border border-emerald-800" : "bg-red-950 text-red-400 border border-red-800"}`}
              >
                {errors.response}
              </div>
            )}

            <Button
              type="submit"
              disabled={!isFormValid() || isLoading || (showBranchField && isLoadingBranches) || !loginEnabled}
              className="w-full h-12 text-base font-bold bg-brand hover:bg-brand-light text-white rounded-xl mt-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {t("login.login_loading")}
                </>
              ) : (
                t("login.login_button")
              )}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={onForgotPasswordClicked}
                disabled={isLoading}
                className="text-sm text-gray-500 hover:text-brand disabled:opacity-50"
              >
                {t("login.forget_pwd_label")}
              </button>
            </div>
          </form>
            </>
          )}
        </div>

        <p className="text-center text-xs text-gray-700 mt-6">{t("copyright")}</p>
      </div>
    </div>
  );
};

export default LoginScreen;
